import React, { useEffect, useState, useRef } from 'react';
import MenuBar from '../Header/MenuBar';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/i18n';
import videosEn from '../../Data/studentJourneyEn';
import videosAr from '../../Data/studentJourneyAr';
import StartTestNudge from '../SharedComponents/StartTestNudge';

// Helper for YouTube thumbnails
const ytThumb = (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;

const StudentJourney = () => {
  const { t } = useTranslation();

  const [active, setActive] = useState(null); // { youtubeId, title } or null
  const [list, setList] = useState(videosEn);
  const overlayRef = useRef(null);

  // --- Language & Direction handling (like your sample) ---
  useEffect(() => {
    const currentLanguage =
      i18n.language || localStorage.getItem('i18nextLng') || 'en';

    if (currentLanguage === 'ar' || currentLanguage?.startsWith('ar')) {
      setList(videosAr);
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      setList(videosEn);
      document.documentElement.setAttribute('dir', 'ltr');
    }
  }, [i18n.language]);

  const direction =
    i18n.language === 'ar' || i18n.language?.startsWith('ar') ? 'rtl' : 'ltr';

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setActive(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Lock scroll while modal is open
  useEffect(() => {
    if (active) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [active]);

  // Click outside to close
  const onOverlayClick = (e) => {
    if (e.target === overlayRef.current) setActive(null);
  };

  return (
    <>
      <MenuBar />

      <div className="TestPage" style={{ minHeight: '100vh' }}>
        <div className="contentTestDesktop before beforeJourney">
          <div className="contentHolder"></div>
        </div>
        <section
          className="stepperHolder stepperHolderJourney"
          style={{ margin: '0 auto', padding: '30 40' }}
        >
          {/* Title & description */}
          <header style={{ marginBottom: 50, marginTop: 20 }}>
            <h1
              style={{ paddingBottom: 20 }}
              className="main-header maindemoheader"
            >
              {t('studentJourney.title')}
            </h1>
            <p
              className="sub-header"
              style={{
                marginTop: 8,
                color: '#555',
                maxWidth: 820,
                fontSize: 16,
              }}
            >
              {t('studentJourney.description')}
            </p>
          </header>

          {/* Cards grid */}
          <div
            style={{
              display: 'grid',
              gap: 25,
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            }}
          >
            {list.map((v) => (
              <article
                key={v.id}
                onClick={() =>
                  setActive({
                    youtubeId: v.youtubeId,
                    title: `${v.student} · ${v.major}`,
                  })
                }
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  (e.key === 'Enter' || e.key === ' ') &&
                  setActive({
                    youtubeId: v.youtubeId,
                    title: `${v.student} · ${v.major}`,
                  })
                }
                aria-label={`Open video ${v.student} – ${v.major}`}
                style={{
                  border: '1px solid #eee',
                  borderRadius: 14,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  background: '#fff',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                  transition: 'transform 200ms ease, box-shadow 200ms ease',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = 'translateY(-2px)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = 'translateY(0)')
                }
              >
                <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                  <img
                    src={ytThumb(v.youtubeId)}
                    alt={`${v.student} – ${v.major}`}
                    loading="lazy"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </div>
                <div style={{ padding: 12 }}>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                    {v.student}
                    <span style={{ color: '#777', fontWeight: 400 }}>
                      {' '}
                      · {v.major}
                    </span>
                  </h3>
                  {v.blurb && (
                    <p style={{ marginTop: 6, color: '#666', fontSize: 14 }}>
                      {v.blurb}
                    </p>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
        <div className="contentTestDesktop after afterJourney">
          <div className="contentHolder">
            {/* <p className="content">{t('skillsPage.content')}</p> */}
          </div>
        </div>
        <StartTestNudge to="/test" />
      </div>

      {/* Modal player */}
      {active && (
        <div
          ref={overlayRef}
          onClick={onOverlayClick}
          role="dialog"
          aria-modal="true"
          aria-label={active.title}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 1000,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 960,
              borderRadius: 16,
              overflow: 'hidden',
              background: '#000',
              boxShadow: '0 10px 40px rgba(0,0,0,0.35)',
              position: 'relative',
            }}
          >
            {/* Close button */}
            <button
              onClick={() => setActive(null)}
              aria-label="Close video"
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 2,
                background: 'rgba(255,255,255,0.9)',
                border: 'none',
                padding: '6px 10px',
                borderRadius: 999,
                cursor: 'pointer',
                fontWeight: 700,
              }}
            >
              ✕
            </button>

            {/* 16:9 responsive iframe */}
            <div style={{ position: 'relative', paddingTop: '56.25%' }}>
              <iframe
                src={`https://www.youtube.com/embed/${active.youtubeId}`}
                title={active.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  border: 0,
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StudentJourney;
